import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import Button from '@mui/material/Button';
import { Close as CloseIcon } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React, { useContext, useState, useEffect } from 'react';
import { I_S3Credentials } from '../../../interfaces/S3Types';
import { I_Installation } from '../../../interfaces/InstallationTypes';
import { InstallationsContext } from '../../../contexts/InstallationsContextProvider';
import { UserContext } from '../../../contexts/userContext';
import routes from '../../../Resources/routes.json';
import { useNavigate } from 'react-router-dom';
import { UserType } from '../../../interfaces/UserTypes';
import axiosConfig from '../../../Resources/axiosConfig';
import {
  getPresetsForDevice,
  PresetConfig,
  BatterySnTree,
  parseBatterySnTree,
  buildEmptyTree,
  remapTree,
  computeFlatValues,
  wouldLoseData,
  SODIOHOME_DEVICE_TYPES,
  buildSodistoreProPreset,
} from './installationSetupUtils';

interface InformationSodistorehomeProps {
  values: I_Installation;
  s3Credentials: I_S3Credentials;
  type?: string;
}

function InformationSodistorehome(props: InformationSodistorehomeProps) {
  if (props.values === null) {
    return null;
  }

  const context = useContext(UserContext);
  const { currentUser } = context;
  const theme = useTheme();
  const intl = useIntl();
  const [formValues, setFormValues] = useState(props.values);
  const requiredFields = ['name'];
  const [openModalDeleteInstallation, setOpenModalDeleteInstallation] =
    useState(false);
  const [pendingPreset, setPendingPreset] = useState<string | null>(null);
  const navigate = useNavigate();

  // Parse inverter/datalogger serial numbers from various legacy formats:
  //   Slash-separated:  "SN001/SN002"
  //   Labeled comma:    "Inverter 1: SN001, Inverter 2: SN002"
  //   Labeled comma:    "Datalogger 1: SN001, Datalogger 2: SN002"
  //   Plain string:     "SN001"
  const parseSerialNumbers = (value: string | undefined): string[] => {
    if (!value || value.trim() === '') return [];

    // Check for labeled comma format: "Inverter 1: SN001, Inverter 2: SN002"
    if (/(?:Inverter|Datalogger)\s*\d+\s*:/i.test(value)) {
      const matches = value.match(/(?:Inverter|Datalogger)\s*\d+\s*:\s*([^,]+)/gi);
      if (matches) {
        return matches
          .map((m) => m.replace(/^(?:Inverter|Datalogger)\s*\d+\s*:\s*/i, '').trim())
          .filter((s) => s !== '');
      }
    }

    // Slash-separated format: "SN001/SN002" — preserve empty positions
    if (value.includes('/')) {
      return value.split('/');
    }

    // Single value
    return [value.trim()];
  };

  const isSodistorePro = props.values.product === 5;
  const DeviceTypes = isSodistorePro
    ? [{ id: 4, name: 'inesco 12K - WR Hybrid' } as const]
    : SODIOHOME_DEVICE_TYPES;

  // Preset state — initializes from persisted installationModel, empty for legacy
  const [selectedPreset, setSelectedPreset] = useState<string>(
    props.values.installationModel || ''
  );
  const [inverterCount, setInverterCount] = useState<string>(
    isSodistorePro && props.values.installationModel
      ? props.values.installationModel
      : ''
  );
  const presetConfig: PresetConfig | null = isSodistorePro
    ? (inverterCount && parseInt(inverterCount, 10) > 0
        ? buildSodistoreProPreset(parseInt(inverterCount, 10))
        : null)
    : (getPresetsForDevice(formValues.device)[selectedPreset] || null);

  const [batterySnTree, setBatterySnTree] = useState<BatterySnTree>(() => {
    if (presetConfig) {
      return parseBatterySnTree(props.values.batterySerialNumbers || '', presetConfig);
    }
    return [];
  });

  const [inverterSerialNumbers, setInverterSerialNumbers] = useState<string[]>(() =>
    parseSerialNumbers(props.values.inverterSN)
  );
  const [dataloggerSerialNumbers, setDataloggerSerialNumbers] = useState<string[]>(() =>
    parseSerialNumbers(props.values.dataloggerSN)
  );

  // PV strings per inverter — persisted as comma-separated, default "1" per inverter
  const [pvStringsPerInverter, setPvStringsPerInverter] = useState<string[]>(() => {
    const stored = props.values.pvStringsPerInverter;
    if (stored && stored.trim() !== '') {
      return stored.split(',').map((s) => s.trim());
    }
    const invCount = presetConfig?.length
      || parseSerialNumbers(props.values.inverterSN).length
      || 1;
    return Array.from({ length: invCount }, () => '1');
  });

  // When presetConfig is available, ensure flat values (batteryClusterNumber, batteryNumber)
  // stay in sync with the current preset structure. This handles:
  // - Legacy installations with device=0 → user sets device type
  // - Preset structure changed (e.g., Growatt home 9 was [[1,1]] → now [[2]])
  useEffect(() => {
    if (!presetConfig) return;

    // Re-parse battery tree if empty but serial numbers exist
    let tree = batterySnTree;
    if (tree.length === 0 && props.values.batterySerialNumbers) {
      tree = parseBatterySnTree(props.values.batterySerialNumbers, presetConfig);
      setBatterySnTree(tree);
    }

    // Always recalculate flat values from current preset to keep DB in sync
    const flat = computeFlatValues(presetConfig, tree);
    if (
      flat.batteryClusterNumber !== formValues.batteryClusterNumber ||
      flat.batteryNumber !== formValues.batteryNumber
    ) {
      setFormValues((prev) => ({ ...prev, ...flat }));
    }
  }, [presetConfig]);

  const handlePresetChange = (newPreset: string) => {
    const newConfig = getPresetsForDevice(formValues.device)[newPreset];
    if (!newConfig) return;

    // Check for data loss — either from existing tree or legacy flat data
    const treeToCheck = batterySnTree.length > 0
      ? batterySnTree
      : props.values.batterySerialNumbers
        ? parseBatterySnTree(props.values.batterySerialNumbers, newConfig)
        : [];
    if (treeToCheck.length > 0 && wouldLoseData(treeToCheck, newConfig)) {
      setPendingPreset(newPreset);
      return;
    }

    applyPreset(newPreset);
  };

  const handlePresetChangeConfirm = () => {
    if (pendingPreset) {
      applyPreset(pendingPreset);
      setPendingPreset(null);
    }
  };

  const handlePresetChangeCancel = () => {
    setPendingPreset(null);
  };

  const applyPreset = (newPreset: string) => {
    const newConfig = getPresetsForDevice(formValues.device)[newPreset];
    if (!newConfig) return;

    setSelectedPreset(newPreset);

    let newTree: BatterySnTree;
    if (presetConfig && batterySnTree.length > 0) {
      // Switching between presets — remap existing tree
      newTree = remapTree(batterySnTree, newConfig);
    } else if (props.values.batterySerialNumbers) {
      // First preset selection on legacy installation — parse existing flat data
      newTree = parseBatterySnTree(props.values.batterySerialNumbers, newConfig);
    } else {
      newTree = buildEmptyTree(newConfig);
    }
    setBatterySnTree(newTree);

    const newInvCount = newConfig.length;
    const newInvSNs = Array.from({ length: newInvCount }, (_, i) =>
      inverterSerialNumbers[i] || ''
    );
    const newDlSNs = Array.from({ length: newInvCount }, (_, i) =>
      dataloggerSerialNumbers[i] || ''
    );
    const newPvStrings = Array.from({ length: newInvCount }, (_, i) =>
      pvStringsPerInverter[i] || '1'
    );
    setInverterSerialNumbers(newInvSNs);
    setDataloggerSerialNumbers(newDlSNs);
    setPvStringsPerInverter(newPvStrings);

    const flat = computeFlatValues(newConfig, newTree);
    setFormValues({
      ...formValues,
      ...flat,
      installationModel: newPreset,
      inverterSN: newInvSNs.join('/'),
      dataloggerSN: newDlSNs.join('/'),
      pvStringsPerInverter: newPvStrings.join(','),
    });
  };

  const handleInverterCountChange = (value: string) => {
    if (value !== '' && !/^\d+$/.test(value)) return;
    if (value !== '' && parseInt(value, 10) > 20) return;
    setInverterCount(value);
    const count = parseInt(value, 10);
    if (isNaN(count) || count < 1) {
      setBatterySnTree([]);
      setFormValues({ ...formValues, installationModel: value });
      return;
    }
    const newConfig = buildSodistoreProPreset(count);
    const newTree = batterySnTree.length > 0
      ? remapTree(batterySnTree, newConfig)
      : buildEmptyTree(newConfig);
    setBatterySnTree(newTree);
    const newInvSNs = Array.from({ length: count }, (_, i) => inverterSerialNumbers[i] || '');
    const newDlSNs = Array.from({ length: count }, (_, i) => dataloggerSerialNumbers[i] || '');
    const newPvStrings = Array.from({ length: count }, (_, i) => pvStringsPerInverter[i] || '1');
    setInverterSerialNumbers(newInvSNs);
    setDataloggerSerialNumbers(newDlSNs);
    setPvStringsPerInverter(newPvStrings);
    const flat = computeFlatValues(newConfig, newTree);
    setFormValues({
      ...formValues,
      ...flat,
      installationModel: value,
      inverterSN: newInvSNs.join('/'),
      dataloggerSN: newDlSNs.join('/'),
      pvStringsPerInverter: newPvStrings.join(','),
    });
  };

  const handleInverterSnChange = (invIdx: number, value: string) => {
    const updated = [...inverterSerialNumbers];
    updated[invIdx] = value;
    setInverterSerialNumbers(updated);
    setFormValues({
      ...formValues,
      inverterSN: updated.join('/'),
    });
  };

  const handleDataloggerSnChange = (invIdx: number, value: string) => {
    const updated = [...dataloggerSerialNumbers];
    updated[invIdx] = value;
    setDataloggerSerialNumbers(updated);
    setFormValues({
      ...formValues,
      dataloggerSN: updated.join('/'),
    });
  };

  const handlePvStringsChange = (invIdx: number, value: string) => {
    if (value !== '' && !/^\d+$/.test(value)) return;
    const updated = [...pvStringsPerInverter];
    updated[invIdx] = value;
    setPvStringsPerInverter(updated);
    setFormValues({
      ...formValues,
      pvStringsPerInverter: updated.join(','),
    });
  };

  const handleBatterySnTreeChange = (
    invIdx: number,
    clIdx: number,
    batIdx: number,
    value: string
  ) => {
    const newTree = batterySnTree.map((inv, i) =>
      i === invIdx
        ? inv.map((cl, j) =>
            j === clIdx
              ? cl.map((bat, k) => (k === batIdx ? value : bat))
              : cl
          )
        : inv
    );
    setBatterySnTree(newTree);
    if (presetConfig) {
      const flat = computeFlatValues(presetConfig, newTree);
      setFormValues({
        ...formValues,
        ...flat,
      });
    }
  };

  const installationContext = useContext(InstallationsContext);
  const {
    updateInstallation,
    deleteInstallation,
    loading,
    setLoading,
    error,
    setError,
    updated,
    setUpdated
  } = installationContext;

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...formValues, [name]: value };

    // When device type changes, reset preset if it's not available for the new device
    if (name === 'device' && !isSodistorePro) {
      const newDevicePresets = getPresetsForDevice(Number(value));
      if (selectedPreset && !newDevicePresets[selectedPreset]) {
        setSelectedPreset('');
        setBatterySnTree([]);
        setInverterSerialNumbers([]);
        setDataloggerSerialNumbers([]);
        setPvStringsPerInverter([]);
        updated.installationModel = '';
        updated.batteryNumber = 0;
        updated.batteryClusterNumber = 0;
        updated.batterySerialNumbers = '';
        updated.inverterSN = '';
        updated.dataloggerSN = '';
        updated.pvStringsPerInverter = '';
      }
    }

    setFormValues(updated);
  };

  const handleSubmit = () => {
    setLoading(true);
    setError(false);
    if (canEdit && installationDateChecklistId !== null) {
      axiosConfig
        .put('/UpdateChecklistItem', null, {
          params: {
            checklistItemId: installationDateChecklistId,
            doneAt: installationDate ?? ''
          }
        })
        .catch(() => {});
    }
    updateInstallation(
      {
        ...formValues,
        couplingType: formValues.couplingType || null,
        backupLoad: formValues.backupLoad || null
      },
      props.type
    );
  };

  const handleDelete = () => {
    setLoading(true);
    setError(false);
    setOpenModalDeleteInstallation(true);
  };

  const deleteInstallationModalHandle = () => {
    setOpenModalDeleteInstallation(false);
    deleteInstallation(formValues, props.type);
    setLoading(false);

    navigate(routes.salidomo_installations + routes.list, {
      replace: true
    });
  };

  const deleteInstallationModalHandleCancel = () => {
    setOpenModalDeleteInstallation(false);
    setLoading(false);
  };

  const areRequiredFieldsFilled = () => {
    for (const field of requiredFields) {
      if (!formValues[field]) {
        return false;
      }
    }
    return true;
  };

  const canEdit = currentUser.userType === UserType.admin;
  const isPartner = currentUser.userType === UserType.partner;

  const [networkProviders, setNetworkProviders] = useState<string[]>([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [installationDate, setInstallationDate] = useState<string>('');
  const [installationDateChecklistId, setInstallationDateChecklistId] = useState<number | null>(null);

  useEffect(() => {
    setLoadingProviders(true);
    axiosConfig
      .get('/GetNetworkProviders')
      .then((res) => setNetworkProviders(res.data))
      .catch(() => {})
      .finally(() => setLoadingProviders(false));
  }, []);

  useEffect(() => {
    if (!props.values.id) return;
    axiosConfig
      .get('/GetChecklistForInstallation', {
        params: { installationId: props.values.id }
      })
      .then((res) => {
        if (Array.isArray(res.data)) {
          const step10 = res.data.find(
            (i: { stepNumber?: number }) => i.stepNumber === 10
          );
          setInstallationDate(step10?.doneAt ?? '');
          setInstallationDateChecklistId(step10?.id ?? null);
        }
      })
      .catch(() => {
        setInstallationDate('');
        setInstallationDateChecklistId(null);
      });
  }, [props.values.id]);

  return (
    <>
      {openModalDeleteInstallation && (
        <Modal
          open={openModalDeleteInstallation}
          onClose={deleteInstallationModalHandleCancel}
          aria-labelledby="error-modal"
          aria-describedby="error-modal-description"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '90vw', sm: 450 },
              bgcolor: 'background.paper',
              borderRadius: 4,
              boxShadow: 24,
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <Typography
              variant="body1"
              gutterBottom
              sx={{ fontWeight: 'bold' }}
            >
              <FormattedMessage
                id="confirmDeleteInstallation"
                defaultMessage="Do you want to delete this installation?"
              />
            </Typography>

            <Typography
              variant="body2"
              sx={{
                mt: 1,
                p: 1,
                bgcolor: '#f5f5f5',
                borderRadius: 1,
                fontFamily: 'monospace',
                wordBreak: 'break-all',
                userSelect: 'all',
                width: '100%',
                textAlign: 'center'
              }}
            >
              {props.s3Credentials.s3Bucket}
            </Typography>

            <Typography
              variant="body2"
              sx={{ mt: 1, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'center' }}
            >
              <FormattedMessage
                id="deleteInstallationWarning"
                defaultMessage="Please note the bucket name above. Purging S3 data may take several minutes. If deletion fails, you can manually remove the bucket from Exoscale."
              />
            </Typography>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginTop: 10
              }}
            >
              <Button
                sx={{
                  marginTop: 2,
                  textTransform: 'none',
                  bgcolor: '#ffc04d',
                  color: '#111111',
                  '&:hover': { bgcolor: '#f7b34d' }
                }}
                onClick={deleteInstallationModalHandle}
              >
                Delete
              </Button>
              <Button
                sx={{
                  marginTop: 2,
                  marginLeft: 2,
                  textTransform: 'none',
                  bgcolor: '#ffc04d',
                  color: '#111111',
                  '&:hover': { bgcolor: '#f7b34d' }
                }}
                onClick={deleteInstallationModalHandleCancel}
              >
                Cancel
              </Button>
            </div>
          </Box>
        </Modal>
      )}

      {pendingPreset !== null && (
        <Modal
          open={pendingPreset !== null}
          onClose={handlePresetChangeCancel}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '90vw', sm: 450 },
              bgcolor: 'background.paper',
              borderRadius: 4,
              boxShadow: 24,
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <Typography
              variant="body1"
              gutterBottom
              sx={{ fontWeight: 'bold', textAlign: 'center' }}
            >
              <FormattedMessage
                id="confirmPresetSwitch"
                defaultMessage="Switching to a smaller configuration will remove some battery serial number entries. Continue?"
              />
            </Typography>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginTop: 10
              }}
            >
              <Button
                sx={{
                  marginTop: 2,
                  textTransform: 'none',
                  bgcolor: '#ffc04d',
                  color: '#111111',
                  '&:hover': { bgcolor: '#f7b34d' }
                }}
                onClick={handlePresetChangeConfirm}
              >
                <FormattedMessage id="continue" defaultMessage="Continue" />
              </Button>
              <Button
                sx={{
                  marginTop: 2,
                  marginLeft: 2,
                  textTransform: 'none',
                  bgcolor: '#ffc04d',
                  color: '#111111',
                  '&:hover': { bgcolor: '#f7b34d' }
                }}
                onClick={handlePresetChangeCancel}
              >
                <FormattedMessage id="cancel" defaultMessage="Cancel" />
              </Button>
            </div>
          </Box>
        </Modal>
      )}

      <Container maxWidth="xl">
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="stretch"
          spacing={3}
        >
          <Grid item xs={12} md={13}>
            <CardContent>
              <Box
                component="form"
                sx={{
                  '& .MuiTextField-root': {
                    m: 1,
                    width: { xs: 'calc(100% - 16px)', md: '50ch' }
                  }
                }}
                noValidate
                autoComplete="off"
              >
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                  <FormattedMessage id="generalInfo" defaultMessage="General Info" />
                </Typography>

                <div>
                  <TextField
                    label={<FormattedMessage id="installation_name" defaultMessage="Installation Name" />}
                    name="name"
                    value={formValues.name}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                    inputProps={{ readOnly: !canEdit }}
                  />
                </div>
                <div>
                  <TextField
                    label={<FormattedMessage id="street" defaultMessage="Street" />}
                    name="street"
                    value={formValues.street || ''}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                    inputProps={{ readOnly: !canEdit }}
                  />
                </div>
                <div>
                  <TextField
                    label={<FormattedMessage id="postCode" defaultMessage="Postcode" />}
                    name="postCode"
                    value={formValues.postCode || ''}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                    inputProps={{ readOnly: !canEdit }}
                  />
                </div>
                <div>
                  <TextField
                    label={<FormattedMessage id="city" defaultMessage="City" />}
                    name="city"
                    value={formValues.city || ''}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                    inputProps={{ readOnly: !canEdit }}
                  />
                </div>
                <div>
                  <TextField
                    label={<FormattedMessage id="canton" defaultMessage="Canton" />}
                    name="canton"
                    value={formValues.canton || ''}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                    inputProps={{ readOnly: !canEdit }}
                  />
                </div>
                <div>
                  <TextField
                    label={<FormattedMessage id="country" defaultMessage="Country" />}
                    name="country"
                    value={formValues.country || ''}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                    inputProps={{ readOnly: !canEdit }}
                  />
                </div>

                {formValues.product === 2 && (
                  <>
                    <div>
                      <TextField
                        label={<FormattedMessage id="firstName" defaultMessage="First name" />}
                        name="firstName"
                        value={formValues.firstName || ''}
                        onChange={handleChange}
                        variant="outlined"
                        fullWidth
                        inputProps={{ readOnly: !canEdit }}
                      />
                    </div>
                    <div>
                      <TextField
                        label={<FormattedMessage id="lastName" defaultMessage="Last name" />}
                        name="lastName"
                        value={formValues.lastName || ''}
                        onChange={handleChange}
                        variant="outlined"
                        fullWidth
                        inputProps={{ readOnly: !canEdit }}
                      />
                    </div>
                  </>
                )}

                <div>
                  <TextField
                    label={<FormattedMessage id="distributionPartner" defaultMessage="Distribution Partner" />}
                    name="distributionPartner"
                    value={formValues.distributionPartner || ''}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                    inputProps={{ readOnly: !canEdit }}
                  />
                </div>

                {formValues.installationModel && (
                  <div>
                    <TextField
                      label={<FormattedMessage id="installationModel" defaultMessage="Installation Model" />}
                      value={formValues.installationModel}
                      variant="outlined"
                      fullWidth
                      inputProps={{ readOnly: true }}
                    />
                  </div>
                )}

                {!isSodistorePro && (
                <div>
                  <FormControl fullWidth sx={{ marginLeft: 1, marginTop: 1, marginBottom: 1, width: { xs: 'calc(100% - 16px)', md: 440 } }}>
                    <InputLabel sx={{ fontSize: 14, backgroundColor: 'white' }}>
                      <FormattedMessage id="DeviceType" defaultMessage="Device Type" />
                    </InputLabel>
                    <Select
                      name="device"
                      value={formValues.device}
                      onChange={handleChange}
                      inputProps={{ readOnly: !canEdit }}
                    >
                      {DeviceTypes.map((device) => (
                        <MenuItem key={device.id} value={device.id}>
                          {device.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
                )}

                <div>
                  <Autocomplete
                    freeSolo
                    options={networkProviders}
                    value={formValues.networkProvider || ''}
                    onChange={(_e, val) =>
                      setFormValues({ ...formValues, networkProvider: (val as string) || '' })
                    }
                    onInputChange={(_e, val) =>
                      setFormValues({ ...formValues, networkProvider: val || '' })
                    }
                    disabled={!canEdit}
                    loading={loadingProviders}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={<FormattedMessage id="networkProvider" defaultMessage="Network Provider" />}
                        variant="outlined"
                        fullWidth
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingProviders ? <CircularProgress size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </div>

                <div>
                  <TextField
                    label={<FormattedMessage id="emailAddress" defaultMessage="Email Address" />}
                    name="email"
                    value={formValues.email || ''}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                    inputProps={{ readOnly: !canEdit }}
                  />
                </div>

                {formValues.product === 2 && (
                  <div>
                    <TextField
                      label={<FormattedMessage id="phone" defaultMessage="Phone number" />}
                      name="phone"
                      value={formValues.phone || ''}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      inputProps={{ readOnly: !canEdit }}
                    />
                  </div>
                )}

                <div>
                  <FormControl fullWidth sx={{ marginLeft: 1, marginTop: 1, marginBottom: 1, width: { xs: 'calc(100% - 16px)', md: 440 } }}>
                    <InputLabel sx={{ fontSize: 14, backgroundColor: 'white' }}>
                      <FormattedMessage id="dataCollectionEnabled" defaultMessage="Data Collection" />
                    </InputLabel>
                    <Select
                      name="dataCollectionEnabled"
                      value={formValues.dataCollectionEnabled === false ? 'no' : 'yes'}
                      onChange={(e) =>
                        setFormValues({
                          ...formValues,
                          dataCollectionEnabled: e.target.value === 'yes'
                        })
                      }
                      inputProps={{ readOnly: !canEdit }}
                    >
                      <MenuItem value="yes"><FormattedMessage id="yes" defaultMessage="Yes" /></MenuItem>
                      <MenuItem value="no"><FormattedMessage id="no" defaultMessage="No" /></MenuItem>
                    </Select>
                  </FormControl>
                </div>

                <div>
                  <FormControl fullWidth sx={{ marginLeft: 1, marginTop: 1, marginBottom: 1, width: { xs: 'calc(100% - 16px)', md: 440 } }}>
                    <InputLabel shrink sx={{ fontSize: 14, backgroundColor: 'white', px: 0.5 }}>
                      <FormattedMessage id="externalEms" defaultMessage="External EMS" />
                    </InputLabel>
                    <Select
                      name="externalEms"
                      value={
                        !formValues.externalEms
                          ? ''
                          : ['No', 'Solar Manager', 'Smart Fox', 'Loxone'].includes(formValues.externalEms)
                          ? formValues.externalEms
                          : 'Other'
                      }
                      onChange={(e) => {
                        const val = e.target.value as string;
                        setFormValues({ ...formValues, externalEms: val });
                      }}
                      inputProps={{ readOnly: !canEdit }}
                      displayEmpty
                      notched
                    >
                      <MenuItem value="">
                        <em><FormattedMessage id="notSet" defaultMessage="Not set" /></em>
                      </MenuItem>
                      <MenuItem value="No"><FormattedMessage id="emsNo" defaultMessage="No" /></MenuItem>
                      <MenuItem value="Solar Manager">Solar Manager</MenuItem>
                      <MenuItem value="Smart Fox">Smart Fox</MenuItem>
                      <MenuItem value="Loxone">Loxone</MenuItem>
                      <MenuItem value="Other"><FormattedMessage id="emsOther" defaultMessage="Other" /></MenuItem>
                    </Select>
                  </FormControl>
                </div>

                {formValues.externalEms &&
                  !['No', 'Solar Manager', 'Smart Fox', 'Loxone'].includes(formValues.externalEms) && (
                  <div>
                    <TextField
                      label={<FormattedMessage id="externalEmsOther" defaultMessage="External EMS (specify)" />}
                      name="externalEms"
                      value={formValues.externalEms === 'Other' ? '' : formValues.externalEms}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      inputProps={{ readOnly: !canEdit }}
                    />
                  </div>
                )}

                {formValues.product === 2 && (
                  <div>
                    <TextField
                      label={<FormattedMessage id="kilowattPeak" defaultMessage="kWp (PV system)" />}
                      name="kilowattPeak"
                      value={formValues.kilowattPeak || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || /^\d{0,5}(\.\d{0,2})?$/.test(val))
                          setFormValues({ ...formValues, kilowattPeak: val });
                      }}
                      variant="outlined"
                      fullWidth
                      inputProps={{ readOnly: !canEdit, inputMode: 'decimal' }}
                    />
                  </div>
                )}

                {(canEdit || isPartner) && (
                  <>
                    <div>
                      <FormControl sx={{ m: 1, width: { xs: 'calc(100% - 16px)', md: '50ch' } }}>
                        <InputLabel
                          shrink
                          sx={{ fontSize: 14, backgroundColor: 'white', px: 0.5 }}
                        >
                          <FormattedMessage id="couplingType" defaultMessage="AC/DC Coupling" />
                        </InputLabel>
                        <Select
                          name="couplingType"
                          value={formValues.couplingType || ''}
                          onChange={handleChange}
                          inputProps={{ readOnly: !canEdit }}
                          displayEmpty
                          notched
                        >
                          <MenuItem value="">
                            <em><FormattedMessage id="notSet" defaultMessage="Not set" /></em>
                          </MenuItem>
                          <MenuItem value="AC">
                            <FormattedMessage id="couplingAC" defaultMessage="AC-coupled" />
                          </MenuItem>
                          <MenuItem value="DC">
                            <FormattedMessage id="couplingDC" defaultMessage="DC-coupled" />
                          </MenuItem>
                          <MenuItem value="Mixed">
                            <FormattedMessage id="couplingMixed" defaultMessage="Mixed" />
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </div>

                    <div>
                      <FormControl sx={{ m: 1, width: { xs: 'calc(100% - 16px)', md: '50ch' } }}>
                        <InputLabel
                          shrink
                          sx={{ fontSize: 14, backgroundColor: 'white', px: 0.5 }}
                        >
                          <FormattedMessage id="backupLoad" defaultMessage="Backup Load" />
                        </InputLabel>
                        <Select
                          name="backupLoad"
                          value={formValues.backupLoad || ''}
                          onChange={handleChange}
                          inputProps={{ readOnly: !canEdit }}
                          displayEmpty
                          notched
                        >
                          <MenuItem value="">
                            <em><FormattedMessage id="notSet" defaultMessage="Not set" /></em>
                          </MenuItem>
                          <MenuItem value="Whole">
                            <FormattedMessage id="backupWhole" defaultMessage="Whole house" />
                          </MenuItem>
                          <MenuItem value="Partial">
                            <FormattedMessage id="backupPartial" defaultMessage="Partial" />
                          </MenuItem>
                          <MenuItem value="None">
                            <FormattedMessage id="backupNone" defaultMessage="Not used" />
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </div>
                  </>
                )}

                <div>
                  <TextField
                    label={<FormattedMessage id="information" defaultMessage="Information" />}
                    name="information"
                    value={formValues.information}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                    inputProps={{ readOnly: !canEdit }}
                  />
                </div>

                {(canEdit || isPartner) && (
                  <>
                    <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
                      <FormattedMessage id="installationSetup" defaultMessage="Installation Setup" />
                    </Typography>

                    <div>
                      <TextField
                        label={<FormattedMessage id="installationSerialNumber" defaultMessage="Installation Serial Number" />}
                        name="serialNumber"
                        value={formValues.serialNumber || ''}
                        onChange={handleChange}
                        variant="outlined"
                        fullWidth
                        inputProps={{ readOnly: !canEdit }}
                      />
                    </div>

                    <div>
                      <TextField
                        label={<FormattedMessage id="installationDate" defaultMessage="Installation Date" />}
                        type="date"
                        value={installationDate}
                        onChange={(e) => setInstallationDate(e.target.value)}
                        variant="outlined"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ readOnly: !canEdit }}
                      />
                    </div>

                    {isSodistorePro ? (
                    <div>
                      <TextField
                        label={<FormattedMessage id="numberOfInverters" defaultMessage="Number of Inverters" />}
                        type="text"
                        value={inverterCount}
                        onChange={(e) => handleInverterCountChange(e.target.value)}
                        variant="outlined"
                        fullWidth
                        inputProps={{ readOnly: !canEdit }}
                      />
                    </div>
                    ) : (
                    <div>
                      <FormControl sx={{ m: 1, width: { xs: 'calc(100% - 16px)', md: '50ch' } }}>
                        <InputLabel
                          shrink
                          sx={{ fontSize: 14, backgroundColor: 'white', px: 0.5 }}
                        >
                          <FormattedMessage id="installationModel" defaultMessage="Installation Model" />
                        </InputLabel>
                        <Select
                          value={selectedPreset}
                          onChange={(e) => handlePresetChange(e.target.value as string)}
                          inputProps={{ readOnly: !canEdit }}
                          displayEmpty
                          notched
                        >
                          <MenuItem value="" disabled>
                            <em><FormattedMessage id="selectModel" defaultMessage="Select model..." /></em>
                          </MenuItem>
                          {Object.keys(getPresetsForDevice(formValues.device)).map((name) => (
                            <MenuItem key={name} value={name}>
                              {name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                    )}

                    <div>
                      <TextField
                        label={<FormattedMessage id="inverterFirmwareVersion" defaultMessage="Inverter Firmware Version" />}
                        name="inverterFirmwareVersion"
                        value={formValues.inverterFirmwareVersion || ''}
                        onChange={handleChange}
                        variant="outlined"
                        fullWidth
                        inputProps={{ readOnly: !canEdit }}
                      />
                    </div>

                    <div>
                      <TextField
                        label={<FormattedMessage id="batteryFirmwareVersion" defaultMessage="Battery Firmware Version" />}
                        name="batteryFirmwareVersion"
                        value={formValues.batteryFirmwareVersion || ''}
                        onChange={handleChange}
                        variant="outlined"
                        fullWidth
                        inputProps={{ readOnly: !canEdit }}
                      />
                    </div>

                    {presetConfig && presetConfig.map((clusters, invIdx) => {
                      const invSn = inverterSerialNumbers[invIdx] || '';
                      const totalBat = clusters.reduce((a, b) => a + b, 0);
                      const filledBat = (batterySnTree[invIdx] || []).flat().filter((s) => s !== '').length;
                      const filledClusters = (batterySnTree[invIdx] || []).filter(
                        (cl) => cl.some((s) => s !== '')
                      ).length;

                      return (
                        <Accordion
                          key={`inv-${invIdx}`}
                          defaultExpanded={false}
                          sx={{ mt: 1 }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                              '& .MuiAccordionSummary-content': { flexGrow: 0, justifyContent: 'flex-start' },
                              justifyContent: 'flex-start',
                              '& .MuiAccordionSummary-expandIconWrapper': { ml: 1 }
                            }}
                          >
                            <Typography sx={{ fontWeight: 'bold' }}>
                              <FormattedMessage id="inverterN" defaultMessage="Inverter {n}" values={{ n: invIdx + 1 }} />
                            </Typography>
                            <Chip
                              label={intl.formatMessage(
                                { id: 'clustersBatteriesSummary', defaultMessage: '{filledClusters}/{totalClusters} clusters, {filledBat}/{totalBat} batteries' },
                                { filledClusters, totalClusters: clusters.length, filledBat, totalBat }
                              )}
                              size="small"
                              variant="outlined"
                              sx={{ ml: 1 }}
                            />
                          </AccordionSummary>
                          <AccordionDetails>
                            <TextField
                              label={intl.formatMessage({ id: 'inverterNSerialNumber', defaultMessage: 'Inverter {n} Serial Number' }, { n: invIdx + 1 })}
                              value={invSn}
                              onChange={(e) => handleInverterSnChange(invIdx, e.target.value)}
                              variant="outlined"
                              fullWidth
                              inputProps={{ readOnly: !canEdit }}
                            />
                            <TextField
                              label={intl.formatMessage({ id: 'dataloggerNSerialNumber', defaultMessage: 'Datalogger {n} Serial Number' }, { n: invIdx + 1 })}
                              value={dataloggerSerialNumbers[invIdx] || ''}
                              onChange={(e) => handleDataloggerSnChange(invIdx, e.target.value)}
                              variant="outlined"
                              fullWidth
                              inputProps={{ readOnly: !canEdit }}
                            />
                            <TextField
                              label={intl.formatMessage({ id: 'pvStringsOnInverterN', defaultMessage: 'Number of PV Strings on Inverter {n}' }, { n: invIdx + 1 })}
                              type="text"
                              value={pvStringsPerInverter[invIdx] || ''}
                              onChange={(e) => handlePvStringsChange(invIdx, e.target.value)}
                              variant="outlined"
                              fullWidth
                              inputProps={{ readOnly: !canEdit }}
                            />

                            {clusters.map((batteryCount, clIdx) => {
                              const filledInCluster = (batterySnTree[invIdx]?.[clIdx] || []).filter((s) => s !== '').length;
                              return (
                              <Accordion
                                key={`cl-${invIdx}-${clIdx}`}
                                defaultExpanded={false}
                                sx={{ mt: 1 }}
                              >
                                <AccordionSummary
                                  expandIcon={<ExpandMoreIcon />}
                                  sx={{
                                    '& .MuiAccordionSummary-content': { flexGrow: 0, justifyContent: 'flex-start' },
                                    justifyContent: 'flex-start',
                                    '& .MuiAccordionSummary-expandIconWrapper': { ml: 1 }
                                  }}
                                >
                                  <Typography sx={{ fontWeight: 'bold' }}>
                                    <FormattedMessage id="clusterN" defaultMessage="Cluster {n}" values={{ n: clIdx + 1 }} />
                                  </Typography>
                                  <Chip
                                    label={intl.formatMessage(
                                      { id: 'batteriesSummary', defaultMessage: '{filled}/{total} batteries' },
                                      { filled: filledInCluster, total: batteryCount }
                                    )}
                                    size="small"
                                    variant="outlined"
                                    sx={{ ml: 1 }}
                                  />
                                </AccordionSummary>
                                <AccordionDetails>
                                  {Array.from({ length: batteryCount }, (_, batIdx) => (
                                    <TextField
                                      key={`bat-${invIdx}-${clIdx}-${batIdx}`}
                                      id={`bat-${invIdx}-${clIdx}-${batIdx}`}
                                      label={intl.formatMessage({ id: 'batteryNSerialNumber', defaultMessage: 'Battery {n} Serial Number' }, { n: batIdx + 1 })}
                                      value={batterySnTree[invIdx]?.[clIdx]?.[batIdx] || ''}
                                      onChange={(e) =>
                                        handleBatterySnTreeChange(invIdx, clIdx, batIdx, e.target.value)
                                      }
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' && presetConfig) {
                                          e.preventDefault();
                                          let nextInv = invIdx, nextCl = clIdx, nextBat = batIdx + 1;
                                          if (nextBat >= presetConfig[invIdx][clIdx]) {
                                            nextBat = 0;
                                            nextCl = clIdx + 1;
                                            if (nextCl >= presetConfig[invIdx].length) {
                                              nextCl = 0;
                                              nextInv = invIdx + 1;
                                              if (nextInv >= presetConfig.length) return;
                                            }
                                          }
                                          const nextId = `bat-${nextInv}-${nextCl}-${nextBat}`;
                                          const nextInput = document.getElementById(nextId) as HTMLInputElement | null;
                                          if (nextInput) nextInput.focus();
                                        }
                                      }}
                                      variant="outlined"
                                      fullWidth
                                      placeholder={canEdit ? 'Scan or enter serial number' : ''}
                                      inputProps={{ readOnly: !canEdit }}
                                    />
                                  ))}
                                </AccordionDetails>
                              </Accordion>
                            ); })}
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </>
                )}

                {canEdit && (
                  <>
                    <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
                      <FormattedMessage id="adminSection" defaultMessage="Admin" />
                    </Typography>

                    <div>
                      <TextField
                        label={<FormattedMessage id="vpnip" defaultMessage="VPN IP" />}
                        name="vpnIp"
                        value={formValues.vpnIp}
                        onChange={handleChange}
                        variant="outlined"
                        fullWidth
                      />
                    </div>
                    <div>
                      <TextField
                        label="S3 Bucket Name"
                        value={formValues.s3BucketId + '-' + (isSodistorePro ? '325c9373-9025-4a8d-bf5a-f9eedf1f155c' : 'e7b9a240-3c5d-4d2e-a019-6d8b1f7b73fa')}
                        variant="outlined"
                        fullWidth
                      />
                    </div>
                    <div>
                      <TextField
                        label="S3 Write Key"
                        value={formValues.s3WriteKey}
                        variant="outlined"
                        fullWidth
                      />
                    </div>
                    <div>
                      <TextField
                        label="S3 Write Secret Key"
                        value={formValues.s3WriteSecret}
                        variant="outlined"
                        fullWidth
                      />
                    </div>
                  </>
                )}

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginTop: 10
                  }}
                >
                  {canEdit && (
                    <Button
                      variant="contained"
                      onClick={handleSubmit}
                      sx={{
                        marginLeft: '10px'
                      }}
                      disabled={!areRequiredFieldsFilled()}
                    >
                      <FormattedMessage
                        id="applyChanges"
                        defaultMessage="Apply Changes"
                      />
                    </Button>
                  )}

                  {canEdit && (
                    <Button
                      variant="contained"
                      onClick={handleDelete}
                      sx={{
                        marginLeft: '10px'
                      }}
                    >
                      <FormattedMessage
                        id="deleteInstallation"
                        defaultMessage="Delete Installation"
                      />
                    </Button>
                  )}

                  {loading && (
                    <CircularProgress
                      sx={{
                        color: theme.palette.primary.main,
                        marginLeft: '20px'
                      }}
                    />
                  )}
                  {error && (
                    <Alert
                      severity="error"
                      sx={{
                        ml: 1,
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <FormattedMessage
                        id="errorOccured"
                        defaultMessage="An error has occurred"
                      />
                      <IconButton
                        color="inherit"
                        size="small"
                        onClick={() => setError(false)}
                        sx={{ marginLeft: '4px' }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Alert>
                  )}
                  {updated && (
                    <Alert
                      severity="success"
                      sx={{
                        ml: 1,
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <FormattedMessage
                        id="successfullyUpdated"
                        defaultMessage="Successfully updated"
                      />

                      <IconButton
                        color="inherit"
                        size="small"
                        onClick={() => setUpdated(false)} // Set error state to false on click
                        sx={{ marginLeft: '4px' }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Alert>
                  )}
                </div>
              </Box>
            </CardContent>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}

export default InformationSodistorehome;
